import { useState, useEffect, useRef } from "react";
import { MessageCircle, Send, X, Loader2, Bot, User, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setLoading(true);

    // Optimistic UI update
    const tempUserMsg = {
      id: Date.now().toString(),
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    
    // Store current messages for rollback
    const previousMessages = messages;
    setMessages([...messages, tempUserMsg]);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/chat/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update with real messages from server
        setMessages(prevMessages => [...prevMessages.slice(0, -1), data.user_message, data.assistant_message]);
        
        // Save conversation ID
        if (!conversationId && data.conversation_id) {
          setConversationId(data.conversation_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar mensaje");
        // Rollback to previous messages
        setMessages(previousMessages);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error al enviar mensaje");
      // Rollback to previous messages
      setMessages(previousMessages);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 sm:w-14 sm:h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50"
        aria-label="Abrir chat"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-96 h-[100dvh] sm:h-[32rem] bg-white sm:rounded-lg shadow-2xl flex flex-col z-50 border-t sm:border border-gray-200">
      {/* Header */}
      <div className="bg-orange-500 text-white p-4 sm:rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot size={24} />
          <div>
            <h3 className="font-semibold text-base">Chat-IA</h3>
            <p className="text-xs text-orange-100">Asistente de reuniones</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-white hover:bg-orange-600"
            onClick={() => setIsOpen(false)}
          >
            <Minimize2 size={18} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-white hover:bg-orange-600"
            onClick={() => {
              setIsOpen(false);
              setMessages([]);
              setConversationId(null);
            }}
          >
            <X size={18} />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <Bot className="text-orange-500 mb-3" size={48} />
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              ¡Hola! Soy tu asistente
            </h4>
            <p className="text-xs text-gray-600">
              Pregúntame sobre tus reuniones, tareas o decisiones
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg, index) => (
              <div
                key={msg.id || index}
                className={`flex items-start gap-2 ${
                  msg.role === "user" ? "flex-row-reverse" : ""
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === "user"
                      ? "bg-orange-500 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {msg.role === "user" ? <User size={14} /> : <Bot size={14} />}
                </div>
                <div
                  className={`max-w-[75%] rounded-lg p-2.5 text-xs ${
                    msg.role === "user"
                      ? "bg-orange-500 text-white"
                      : "bg-white text-gray-900 border border-gray-200"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  <p
                    className={`text-[10px] mt-1 ${
                      msg.role === "user" ? "text-orange-100" : "text-gray-500"
                    }`}
                  >
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                  <Bot size={14} />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-2.5">
                  <Loader2 className="animate-spin text-gray-500" size={16} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="border-t p-3 bg-white rounded-b-lg">
        <form onSubmit={sendMessage} className="flex gap-2">
          <Input
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Escribe aquí..."
            disabled={loading}
            className="flex-1 text-sm h-9"
          />
          <Button
            type="submit"
            disabled={!inputMessage.trim() || loading}
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 h-9 px-3"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <Send size={16} />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
